import { motion } from 'framer-motion'
import { useState, useEffect, useRef } from 'react'
import { devLog, logAngle, logWheelState, logAnimation } from '../utils/logger'

const Wheel = ({ items, isSpinning, onSpinComplete }) => {
  // 转盘累计旋转角度，每次转动都会累加
  const [rotation, setRotation] = useState(0)
  
  // 记录目标结果，用于动画结束时回调
  const targetResultRef = useRef(null)

  // 监听转动指令
  useEffect(() => {
    if (isSpinning) {
      // 1. 计算总权重
      const totalWeight = items.reduce((sum, item) => sum + (item.weight || 1), 0)
      
      // 2. 随机选择目标
      const randomValue = Math.random() * totalWeight
      let currentWeight = 0
      let targetIndex = 0
      
      for (let i = 0; i < items.length; i++) {
        currentWeight += (items[i].weight || 1)
        if (randomValue <= currentWeight) {
          targetIndex = i
          break
        }
      }
      
      // 保存结果到 ref，供动画结束时使用
      targetResultRef.current = items[targetIndex]

      // 3. 计算角度细节
      const segmentAngles = items.map(item => (item.weight || 1) / totalWeight * 360)
      
      // 计算目标扇形的起始角度
      let targetSegmentStart = 0
      for (let i = 0; i < targetIndex; i++) {
        targetSegmentStart += segmentAngles[i]
      }
      
      // 目标扇形内的随机偏移（让指针指在扇形内的随机位置，而不是正中间）
      // 加上 5% 的 buffer 防止指在边界线上
      const offset = segmentAngles[targetIndex] * 0.1
      const randomOffset = offset + Math.random() * (segmentAngles[targetIndex] - 2 * offset)
      
      // 目标点在转盘上的原始角度 (0-360)
      const targetAngleInWheel = targetSegmentStart + randomOffset

      devLog(`Target: ${items[targetIndex].text}, Angle: ${targetAngleInWheel.toFixed(2)}`)

      // 4. 计算目标旋转角度
      // 公式：当前角度 + (补齐一圈) + (多转 N 圈) - 目标角度
      // 减去目标角度是因为：为了让 30度的点转到 0度，需要顺时针转 -30度 (或 330度)
      setRotation(prevRotation => {
        const extraSpins = 6 // 至少多转 5 圈
        const spinAngle = 360 * extraSpins
        
        // 核心算法：
        // 1. prevRotation: 当前位置
        // 2. (360 - prevRotation % 360): 补齐当前圈，回到 0 度 (逻辑上的 0 度)
        // 3. spinAngle: 多转几圈
        // 4. -targetAngleInWheel: 回退目标角度，使目标对准上方指针
        
        const nextRotation = prevRotation + (360 - prevRotation % 360) + spinAngle - targetAngleInWheel
        
        logAnimation('开始', { targetIndex, nextRotation })
        return nextRotation
      })
    }
  }, [isSpinning, items])

  return (
    <div className="relative">
      {/* 转盘主体 - 使用motion.div实现旋转动画 */}
      <motion.div
        className="w-80 h-80 rounded-full border-8 border-white/20 shadow-2xl relative overflow-hidden"
        animate={{ rotate: rotation }}
        transition={{ 
          duration: 8,
          ease: [0.2, 0.1, 0, 1], // 贝塞尔曲线：先慢后快再慢，模拟物理阻力
          type: "tween"
        }}
        onAnimationComplete={() => {
          if (isSpinning && targetResultRef.current) {
            logAnimation('结束', { result: targetResultRef.current })
            onSpinComplete(targetResultRef.current)
          }
        }}
        style={{ transformOrigin: 'center' }}
      >

        {items.map((item, index) => {
          // 计算当前扇形的起始角度
          // 注意：这里需要重新计算一遍角度分布，虽然有点重复但为了渲染准确是必要的
          // 也可以用 useMemo 优化，但考虑到 items 改变不频繁，直接算也没问题
          const totalWeight = items.reduce((sum, i) => sum + (i.weight || 1), 0)
          const segmentAngles = items.map(i => (i.weight || 1) / totalWeight * 360)
          
          let startAngle = 0
          for (let i = 0; i < index; i++) {
            startAngle += segmentAngles[i]
          }
          
          const midAngle = startAngle + segmentAngles[index] / 2
          
          return (
            <div
              key={item.id}
              className="absolute inset-0"
              style={{
                background: `conic-gradient(from ${startAngle}deg, ${item.color} 0deg, ${item.color} ${segmentAngles[index]}deg, transparent ${segmentAngles[index]}deg)`
              }}
            >
              {/* 扇形内的文字 */}
              <div
                className="absolute text-white font-bold text-lg"
                style={{
                  top: '50%',
                  left: '50%',
                  transform: `translate(-50%, -50%) rotate(${midAngle}deg) translateY(-120px)`,
                  transformOrigin: 'center',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                }}
              >
                {segmentAngles[index] < 30 ? item.text.slice(0, 1) : item.text}
              </div>
            </div>
          )
        })}
        
      </motion.div>

      {/* 指针 - 指向正上方 */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '-10px', // 调整位置让指针尖端刚好指着圆周
          transform: 'translate(-50%, 0)',
          width: '40px',
          height: '60px',
          zIndex: 30,
          animation: isSpinning ? 'pointerSpin 0.5s infinite ease-in-out' : 'none'
        }}
      >
        <svg width="40" height="60" viewBox="0 0 40 60" style={{ filter: 'drop-shadow(0 4px 4px rgba(0,0,0,0.2))' }}>
          <polygon 
            points="20,60 5,0 35,0" 
            fill="#ef4444"
            stroke="white"
            strokeWidth="2"
          />
        </svg>
      </div>

      {/* 转盘中心装饰圆 */}
      <div 
        className="absolute top-1/2 left-1/2 w-16 h-16 bg-white rounded-full transform -translate-x-1/2 -translate-y-1/2 shadow-lg flex items-center justify-center border-4 border-gray-100"
        style={{ zIndex: 20 }}
      >
        <div className="w-10 h-10 bg-gradient-to-br from-theme-400 to-theme-600 rounded-full shadow-inner"></div>
      </div>
    </div>
  )
}

export default Wheel
